interface WatermarkOptions {
  text: string | string[]
  font?: string
  color?: string
  rotate?: number
  opacity?: number
  gapX?: number
  gapY?: number
  zIndex?: number
}

class Watermark {
  private el: HTMLDivElement | null = null
  private observer: MutationObserver | null = null
  private timer: ReturnType<typeof setInterval> | null = null
  private options: WatermarkOptions

  constructor(options: WatermarkOptions) {
    this.options = {
      font: '10px Arial',
      color: 'rgb(128,128,128)',
      rotate: -45,
      opacity: 0.1,
      gapX: 200,
      gapY: 150,
      zIndex: 9999,
      ...options
    }
  }

  private parseFont(font: string) {
    const normalized = String(font || '').trim()
    const match = normalized.match(/(\d+(?:\.\d+)?)px/i)
    if (!match || typeof match.index !== 'number') {
      return { size: 10, prefix: '', suffix: normalized || 'Arial' }
    }

    const size = Number(match[1])
    const prefix = normalized.slice(0, match.index).trim()
    const suffix = normalized.slice(match.index + match[0].length).trim()

    return {
      size: Number.isFinite(size) ? size : 10,
      prefix,
      suffix: suffix || 'Arial'
    }
  }

  private measureMaxLineWidth(lines: string[], font: string) {
    const measureCanvas = document.createElement('canvas')
    const measureCtx = measureCanvas.getContext('2d')
    if (!measureCtx) return 0
    measureCtx.font = font
    let maxTextWidth = 0
    for (const line of lines) {
      maxTextWidth = Math.max(maxTextWidth, measureCtx.measureText(line).width)
    }
    return maxTextWidth
  }

  private normalizeTextLines(text: string | string[]) {
    if (Array.isArray(text)) return text.filter(Boolean)
    const raw = String(text || '').trim()
    if (!raw) return []
    return [raw]
  }

  /** 创建 canvas 水印 */
  private createWatermarkBase64() {
    const { text, font, color, rotate, opacity, gapX, gapY } = this.options

    const resolvedFont = font || '10px Arial'
    const resolvedGapX = Math.max(1, Number(gapX || 320))
    const resolvedGapY = Math.max(1, Number(gapY || 220))
    const resolvedRotate = Number(rotate || -45)
    const lines = this.normalizeTextLines(text)

    const paddingX = 24
    const paddingY = 18
    const tileW = resolvedGapX
    const tileH = resolvedGapY

    const { prefix, suffix, size } = this.parseFont(resolvedFont)
    const minFontSize = 6
    let effectiveSize = Math.max(minFontSize, Math.floor(size || 10))
    const rad = (Math.PI / 180) * resolvedRotate
    const cos = Math.abs(Math.cos(rad))
    const sin = Math.abs(Math.sin(rad))

    while (effectiveSize > minFontSize) {
      const candidateFont = `${prefix ? `${prefix} ` : ''}${effectiveSize}px ${suffix}`
      const maxTextWidth = this.measureMaxLineWidth(lines, candidateFont)
      const lineHeight = Math.ceil(effectiveSize * 1.4)
      const baseW = Math.ceil(maxTextWidth + paddingX * 2)
      const baseH = Math.ceil(lineHeight + paddingY * 2)
      const bboxW = Math.ceil(baseW * cos + baseH * sin)
      const bboxH = Math.ceil(baseW * sin + baseH * cos)

      if (bboxW <= tileW && bboxH <= tileH) {
        break
      }
      effectiveSize -= 1
    }

    const effectiveFont = `${prefix ? `${prefix} ` : ''}${effectiveSize}px ${suffix}`

    const canvas = document.createElement('canvas')
    const ratio = window.devicePixelRatio || 1

    canvas.width = tileW * ratio
    canvas.height = tileH * ratio

    const ctx = canvas.getContext('2d')!
    ctx.scale(ratio, ratio)
    ctx.globalAlpha = opacity!
    ctx.fillStyle = color!
    ctx.font = effectiveFont
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'

    ctx.translate(tileW / 2, tileH / 2)
    ctx.rotate(rad)

    ctx.fillText(lines[0] || '', 0, 0)

    const base64 = canvas.toDataURL()
    return base64
  }

  /** 创建水印层 */
  private createLayer() {
    const base64 = this.createWatermarkBase64()
    const div = document.createElement('div')
    div.className = 'watermark-layer'

    Object.assign(div.style, {
      position: 'fixed',
      left: '0',
      top: '0',
      width: '100%',
      height: '100%',
      pointerEvents: 'none',
      backgroundImage: `url(${base64})`,
      backgroundRepeat: 'repeat',
      zIndex: String(this.options.zIndex)
    })
    document.body.appendChild(div)
    this.el = div
  }

  /** 防删除 */
  private protect() {
    if (!this.el) return

    this.observer = new MutationObserver(() => {
      if (!document.body.contains(this.el!)) {
        document.body.appendChild(this.el!)
      }
    })

    this.observer.observe(document.body, {
      childList: true
    })

    // 防篡改
    this.timer = setInterval(() => {
      if (!this.el) return

      const style = window.getComputedStyle(this.el)

      if (style.display === 'none') {
        this.el.style.display = 'block'
      }

      if (style.zIndex !== String(this.options.zIndex)) {
        this.el.style.zIndex = String(this.options.zIndex)
      }
    }, 2000)
  }

  /** resize / 全屏 重绘 */
  private bindResize() {
    window.addEventListener('resize', this.redraw)
    document.addEventListener('fullscreenchange', this.redraw)
  }

  private redraw = () => {
    this.destroy()
    this.createLayer()
    this.protect()
  }

  /** 创建水印 */
  create() {
    this.createLayer()
    this.protect()
    this.bindResize()
  }

  /** 更新水印 */
  update(text: string | string[]) {
    this.options.text = text
    this.redraw()
  }

  /** 更新配置 */
  updateOptions(options: Partial<WatermarkOptions>) {
    this.options = {
      ...this.options,
      ...options
    }
    this.redraw()
  }

  /** 销毁 */
  destroy() {
    if (this.el) {
      this.el.remove()
      this.el = null
    }

    if (this.observer) {
      this.observer.disconnect()
      this.observer = null
    }

    if (this.timer) {
      clearInterval(this.timer)
      this.timer = null
    }

    window.removeEventListener('resize', this.redraw)
    document.removeEventListener('fullscreenchange', this.redraw)
  }
}

export default Watermark
